// ------------------------------------------------------------------------------
// CommonLogin
// author: mudas
// created: 2025/3/29
// ------------------------------------------------------------------------------

import React, { FC, ReactNode, useEffect, useState } from 'react';
import { useResponsive } from 'ahooks';
import { useLocation, useNavigate, useParams } from 'react-router';
import { encodeNextPath, getNextPath, isOutPlatform, loginByOpenId, LoginPayload, useComputed } from 'common/hooks';
import { getEnv, openURL } from 'common/utils';

import { TiangongThemeType } from 'common/types';
import { ROUTER_PATHNAME } from 'common/router/config';

import { CloseOutlined } from '@ant-design/icons';
import { Button, ConfigProvider, Divider, Tabs, Tooltip } from '@fuxi/eevee-ui';
import MyIcon from 'common/components/EeveeIcon';
import PhoneLogin from './PhoneLogin';
import EmailLogin from './EmailLogin';

import cx from './CommonLogin.module.css';

export interface IProps {
  // 当前使用的主题类型
  theme?: TiangongThemeType;
  onSuccess?: (value: LoginPayload) => void;

  // 登录页根路径类型（主要用于区分在对外环境包含对内、对外两种风格登录界面）
  rootPath?: ROUTER_PATHNAME;

  // 是否展示右上角关闭按钮
  closable?: boolean;

  // 点击关闭后跳转的目标页面
  closedURL?: string;
}

const WIDTH = 528;
const HEIGHT = 648;

export const LoginWrapper = ({
  className,
  children,
  closable = true,
  closedURL = `/${ROUTER_PATHNAME.HOME}`,
}: {
  className?: string;
  children?: ReactNode;
  closable?: boolean;
  closedURL?: string;
}) => {
  const responsive = useResponsive();
  const formSize = useComputed(() => {
    if (responsive.xl) {
      return {
        width: WIDTH * 1.5,
        height: HEIGHT * 1.5,
      };
    } else {
      return {
        width: WIDTH,
        height: HEIGHT,
      };
    }
  }, [responsive.xl]);

  return (
    <div
      className={cx('custom-login-form', responsive.xl && 'custom-login-form-large', className)}
      style={{ width: formSize.width }}>
      <header className={cx('header')}>
        <div className={cx('title')}>
          欢迎登录
          {closable && <CloseOutlined className={cx('close')} onClick={() => openURL(closedURL, '_self')} />}
        </div>
        <div className={cx('description')}>登录后即可体验美术一站式服务</div>
      </header>
      <div className={cx('content')}>{children}</div>
    </div>
  );
};

const CommonLogin: FC<IProps> = (props) => {
  const { theme, rootPath, closable = true, closedURL = `/${ROUTER_PATHNAME.HOME}`, onSuccess } = props;

  const { isInternal } = getEnv();
  const loginRoot = rootPath || ROUTER_PATHNAME.LOGIN;

  const responsive = useResponsive();
  const nav = useNavigate();
  const location = useLocation();
  const { type } = useParams();

  const [activeTab, setActiveTab] = useState(type && ['mobile', 'mail'].includes(type) ? type : 'mobile');

  const items = useComputed(() => [
    {
      key: 'mobile',
      label: '手机号登录',
      children: <PhoneLogin theme={theme} onSuccess={onSuccess} />,
    },
    {
      key: 'mail',
      label: '邮箱登录',
      children: <EmailLogin theme={theme} onSuccess={onSuccess} />,
      forceRender: true,
    },
  ]);

  const handleLoginByOpenId = () => {
    const { search, origin } = window.location;
    const next = getNextPath();
    const params = new URLSearchParams(search);

    params.set('next', encodeNextPath(next));

    let url: string;

    if (isOutPlatform(params)) {
      // 外部平台直接跳转目标页面
      url = `${origin}/${ROUTER_PATHNAME.OPENID_LOGIN}?${params.toString()}`;
    } else {
      // 天工对外平台先跳转到中转页面，对内平台直接跳转路由地址
      url = !isInternal ? `${origin}/${ROUTER_PATHNAME.OPENID_LOGIN}?${params.toString()}` : `${origin}${next ?? '/'}`;
    }

    loginByOpenId(url);
  };

  useEffect(() => {
    nav(`/${loginRoot}/${activeTab}${location.search}`, { replace: true });
  }, [activeTab]);

  return (
    <ConfigProvider
      theme={{
        components: {
          Tabs: {
            titleFontSize: 20,
            titleFontSizeLG: 30,
            lineWidthBold: 4,
            inkBarColor: 'var(--color-primary)',
          },
        },
      }}>
      <div className={cx('custom-login-main')}>
        <LoginWrapper closable={closable} closedURL={closedURL}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={items}
            size={responsive.xl ? 'large' : 'middle'}
            indicator={{
              size: (origin) => {
                if (activeTab === 'mobile') {
                  return 0.5 * origin;
                } else {
                  return 0.4 * origin;
                }
              },
              align: 'start',
            }}
          />

          <div className={cx('openID-login')}>
            <Divider className={cx('openID-login-divider')}>更多方式</Divider>
            <Tooltip title="corp或mesg.corp邮箱登录">
              <Button
                icon={<MyIcon size={24} icon="open-id" style={{ marginBottom: -4 }} />}
                size="large"
                type={'text'}
                onClick={handleLoginByOpenId}
                style={{ flex: 1 }}>
                使用 OpenID 登录
              </Button>
            </Tooltip>
          </div>
        </LoginWrapper>
      </div>
    </ConfigProvider>
  );
};

export default CommonLogin;
